import type { Column } from '../../claim-history/simple-table'
import type { UseQueryResult } from '@tanstack/react-query'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { MdCopyAll } from 'react-icons/md'
import { Link } from 'react-router-dom'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { useTableSort } from '../../claim-history/simple-table/use-sort-hook'
import * as Card from '../../shared/card'
import { FriendlyDateTime } from '../../shared/Date/friendly-date-time'

type LogTypes = 'import' | 'eligibility-export' | 'rx-requests-export' | 'create-requests'
type StatusTypes = 'failed' | 'success'

const typeMap: Record<LogTypes, string> = {
  'import': 'Import Prescriptions',
  'eligibility-export': 'Eligibility Export',
  'rx-requests-export': 'Rx Requests Export',
  'create-requests': 'Create requests'
}

const statusMap: Record<StatusTypes, string> = {
  failed: 'Failed',
  success: 'Success'
}

interface useUbaAuditLogProps {
  page: number
  count: number
  dir: string
  on: keyof AuditLog
  filters: {type: string[], status: string[]}
}

interface AuditLog extends Record<string, unknown>{
  id: string
  file_name: string
  patient_id: string
  patient_name: string
  type: LogTypes
  status: string
  log: Array<{ title: string, file_name: string, payload: { output: Record<string, unknown>, input: Record<string, unknown> }, err: string }>
  created_at: string
  client_name: string
}

interface QueryData {
  data: AuditLog[]
  total: number
  count: number
}

function useUbaAuditLog ({ page, count, dir, on, filters }: useUbaAuditLogProps): UseQueryResult<QueryData, unknown> {
  return useQuery<QueryData, unknown, QueryData, ['uba-audit-log', { page: number, count: number, dir: string, on: keyof AuditLog, filters: {type: string[], status: string[]}}]>({
    queryKey: [ 'uba-audit-log', { page, count, dir, on, filters }],
    queryFn: async () => {
      let sp = new URLSearchParams([
        [ 'count', count.toString() ],
        [ 'page', page.toString() ],
        [ 'dir', dir.toString() ],
        [ 'on', on.toString() ],
        ...filters.type.map((v) => [ 'type', v ]),
        ...filters.status.map((v) => [ 'status', v ])
      ])

      return await http.getRequest(`/admin/uba-audit-log?${sp.toString()}`)
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })
}

export function UbaAuditLog (): JSX.Element {
  const { sort, setSort } = useTableSort<AuditLog>({ field: 'created_at', direction: 'desc' })
  let [ paging, setPaging ] = useState<{ page: number, pageSize: number }>({ page: 0, pageSize: 10 })
  let [ filters, setFilters ] = useState<{ type: string[], status: string[] }>({
    type: [],
    status: []
  })

  const query = useUbaAuditLog({
    page: paging.page,
    count: paging.pageSize,
    on: sort.field,
    dir: sort.direction,
    filters
  })

  let columns = useMemo<Array<Column<AuditLog>>>(() => [
    {
      name: 'created_at',
      header: 'Created at',
      isSortable: true,
      getValue: (item) => {
        return (
          <FriendlyDateTime
            short
            datetime={item.created_at}
          />
        )
      }
    },
    {
      name: 'type',
      header: 'Type',
      getValue: (item) => {
        return typeMap?.[item.type] ?? ''
      }
    },
    {
      name: 'file_name',
      header: 'File Name'
    },
    {
      name: 'status',
      header: 'Status',
      getValue: ({ status }) => {
        return status === 'failed' ? 'Failed' : 'Success'
      }
    },
    {
      name: 'patient_name',
      header: 'Patient Name',
      getValue: (item) => {
        return (
          <Link to={`/patient/${item.client_name}/${item.patient_id}`}>{item.patient_name}</Link>
        )
      }
    }
  ], [])

  return (
    <Card.Card>
      <Card.Header>
        <h2>Audit Log</h2>
      </Card.Header>
      <Table.Table
        data={query?.data?.data ?? []}
        filters={[
          {
            name: 'type',
            value: filters.type,
            options: (Object.keys(typeMap) as LogTypes[]).map((item) => {
              return {
                value: item,
                label: typeMap[item]
              }
            })
          },
          {
            name: 'status',
            value: filters.status,
            options: (Object.keys(statusMap) as StatusTypes[]).map((item) => {
              return {
                value: item,
                label: statusMap[item]
              }
            })
          }
        ]}
        queryStatus={query.status}
        onFiltersClear={() => setFilters({ type: [], status: [] })}
        noDataPlaceholder='No records found'
        sort={sort}
        onFilterChange={(field, values) => {
          setFilters((prev) => ({ ...prev, [field]: values }))
        }}
        columns={columns}
        pagination={{ page: paging.page, pageSize: paging.pageSize, totalFiltered: query.data?.total }}
        onSortChange={(field, direction) => setSort({ field, direction })}
        onPageChange={(page) => setPaging((prev) => ({ ...prev, page: page - 1 }))}
        onPageSizeChange={(size) => setPaging((prev) => ({ pageSize: size, page: 0 }))}
        expandingComponent={(item) => <RowBody item={item} />}
      />
    </Card.Card>
  )
}

interface RowBodyProps {
  item: AuditLog
}

export function RowBody ({ item }: RowBodyProps): JSX.Element {
  function handleCopy (string: string): void {
    void navigator.clipboard.writeText(string)
      .then(() => {
        toast.success('Copied to clipboard', { position: 'bottom-center' })
      })
  }

  return (
    <div>
      {item.log.map((l) => (
        <div
          key={l.title}
          className='maw-w-full'
        >
          <h3>{l.title}</h3>
          <div className='relative group'>
            <pre
              className='bg-gray-100 max-h-60 m-0 w-full overflow-y-scroll no-wrap inline-block text-ellipsis p-4 rounded-md'
            >
              {JSON.stringify(l.payload ?? l.err, null, 2)}
            </pre>
            <button
              onClick={() => handleCopy(JSON.stringify(l.payload ?? l.err, null, 2))}
              className='flex justify-center items-center absolute right-2 top-2 w-6 h-6 border-1 border-gray-300 cursor-pointer border-solid rounded-md opacity-0 group-hover:opacity-100 transition-opacity'
            >
              <MdCopyAll />
            </button>
          </div>

        </div>

      ))}
    </div>
  )
}
